import { useDebouncedValue, useInputState } from '@mantine/hooks'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { fetchAllAssets } from '@/services/stock/stocks'
import { StockAssetPreview } from '@/types/stock'

export interface StockItem {
  assetId: string
  name: string
  symbol: string
  slug: string
  logo: string | null
}

export const allStocksQueryOptions = queryOptions({
  queryKey: ['allStocks'],
  queryFn: () => fetchAllAssets(),
  staleTime: 1000 * 60 * 60, // 1 hour
})

export const useAllStocks = () => useQuery(allStocksQueryOptions)

const transformToStockItem = (stock: StockAssetPreview): StockItem => ({
  assetId: stock.asset_id,
  name: stock.name ?? stock.symbol,
  symbol: stock.symbol,
  slug: stock.slug,
  logo: stock.logo,
})

export function useStockList() {
  const { data: allStocks, isFetching, isLoading } = useAllStocks()

  const stockList = useMemo<StockItem[]>(() => {
    if (!allStocks) return []
    return allStocks.map(transformToStockItem)
  }, [allStocks])

  return {
    stockList,
    isLoading: isFetching || isLoading,
  }
}

export function useSearchStocks() {
  const [keyword, setKeyword] = useInputState<string>('')
  const [debouncedKeyword] = useDebouncedValue(keyword, 300)
  const { stockList, isLoading: isLoadingStocks } = useStockList()
  const filteredStocks = useMemo(() => {
    if (!debouncedKeyword) return stockList
    const lowerKeyword = debouncedKeyword.toLowerCase()
    return stockList.filter(
      stock =>
        stock.name.toLowerCase().includes(lowerKeyword) ||
        stock.symbol.toLowerCase().includes(lowerKeyword),
    )
  }, [debouncedKeyword, stockList])

  return {
    keyword,
    setKeyword,
    stockList: filteredStocks,
    isLoading: isLoadingStocks,
  }
}
